import React, { useState, useEffect } from 'react';
import { Container, Box, Typography, Button } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ExerciseCategorySelection from '../../components/Exercise/ExerciseCategorySelection';
import ExerciseTypeSelection from '../../components/Exercise/ExerciseTypeSelection';
import ExerciseCommonFields from '../../components/Exercise/ExerciseCommonFields';
import ExerciseMaterialSelector from '../../components/Exercise/ExerciseMaterialSelector';
import MathArit2Operand1PhaseConfig from '../../components/Exercise/MathArit2Operand1Phase/MathArit2Operand1PhaseConfig';
import exerciseCategories from '../../types/exercise.category.json';
import exerciseTypesData from '../../types/exercise.type.json';
import { createExercise, ExerciseUpsertPayload, getExercises } from '../../services/exerciseService'; // Import createExercise service
import Breadcrumb from '../../components/Breadcrumb';
import MathAritRoundingConfig from '../../components/Exercise/MathAritRounding/MathAritRoundingConfig';
import FillInBlanksConfig from '../../components/Exercise/FillInTheBlanks/FillInBlanksConfig';
import PairingConfig from '../../components/Exercise/Pairing/PairingConfig';
import MaterialBasedConfig from '../../components/Exercise/MaterialBased/MaterialBasedConfig';
import MaterialBasedMultichoiceConfig from '../../components/Exercise/MaterialBasedMultichoice/MaterialBasedMultichoiceConfig';
import SortToBoxesConfig from '../../components/Exercise/SortToBoxes/SortToBoxesConfig';
import OrderingConfig from '../../components/Exercise/Ordering/OrderingConfig';
import AuthoringAssistantPanel from '../../components/AI/AuthoringAssistantPanel';
import { AuthoringAssistantGeneratedDraftResponse } from '../../services/authoringAssistantService';
import { getAuthoringAssistantCreateState } from '../../components/AI/authoringAssistantNavigation';

const ExerciseCreatePage: React.FC = () => {
  const { lessonId } = useParams<{ lessonId: string }>();
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const parsedLessonId = lessonId ? parseInt(lessonId, 10) : null;
  const parsedCourseId = courseId ? parseInt(courseId, 10) : undefined;
  const assistantCreateState = getAuthoringAssistantCreateState(location.state, 'exercise');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [exerciseConfig, setExerciseConfig] = useState<any>({});
  const [configRenderVersion, setConfigRenderVersion] = useState(0);
  const [nextOrder, setNextOrder] = useState(1);

  // Common exercise fields state
  const [commonFields, setCommonFields] = useState({
    instructions: '',
    additionalInfo: '',
    order: 1,
    maxScore: 100,
    requiresManualChecking: false,
    allowReset: true,
    showCorrectAnswers: true,
  });

  // Fetch max order on component mount
  useEffect(() => {
    const fetchMaxOrder = async () => {
      if (parsedLessonId) {
        try {
          const response = await getExercises(parsedLessonId);
          const exercises = response.data;
          if (exercises.length > 0) {
            const maxOrder = Math.max(...exercises.map((e: any) => e.order || 0));
            setNextOrder(maxOrder + 1);
            setCommonFields(prev => ({ ...prev, order: maxOrder + 1 }));
          }
        } catch (error) {
          console.error('Failed to fetch exercises:', error);
        }
      }
    };
    fetchMaxOrder();
  }, [parsedLessonId]);

  React.useEffect(() => {
    console.log("selectedType changed:", selectedType);
  }, [selectedType]);

  React.useEffect(() => {
    console.log("commonFields changed:", commonFields);
  }, [commonFields]);

  const handleSelectCategory = (category: string) => {
    setSelectedCategory(category);
    setSelectedType(null); // Reset type selection when category changes
    setCommonFields({
      instructions: '',
      additionalInfo: '',
      order: nextOrder,
      maxScore: 100,
      requiresManualChecking: false,
      allowReset: true,
      showCorrectAnswers: true,
    });
  };

  const handleSelectType = (typeCode: string) => {
    setSelectedType(typeCode);
    setExerciseConfig({});
    setConfigRenderVersion(0);
    setCommonFields({
      instructions: '',
      additionalInfo: '',
      order: nextOrder,
      maxScore: 100,
      requiresManualChecking: false,
      allowReset: true,
      showCorrectAnswers: true,
    });
  };

  const handleCommonFieldChange = (field: string, value: any) => {
    setCommonFields(prev => ({
      ...prev,
      [field]: field === "requiresManualChecking" ? Boolean(value) : value,
    }));
  };

  const handleConfigChange = React.useCallback((config: any) => {
    setExerciseConfig((previousConfig: any) => ({
      ...previousConfig,
      ...config,
    }));
  }, []);

  const handleMaterialChange = (materialId?: number, showMaterialToStudent?: boolean) => {
    setExerciseConfig((previousConfig: any) => ({
      ...previousConfig,
      materialId,
      showMaterialToStudent,
    }));
  };

  const usesBuiltInMaterialSelection = [
    'material_based_ai_assisted',
    'material_based_multichoice',
    'reading_comprehension_multichoice',
    'true_false',
  ].includes(selectedType || '');

  const handleApplyDraft = (draft: AuthoringAssistantGeneratedDraftResponse) => {
    if (!draft.exercise) {
      return;
    }

    const generatedType = draft.exercise.type;
    const generatedExerciseType = generatedType
      ? exerciseTypesData.find((type) => type.exerciseTypeCode === generatedType)
      : undefined;
    const isChangingType = Boolean(generatedExerciseType && generatedType !== selectedType);

    if (generatedExerciseType) {
      setSelectedCategory(generatedExerciseType.exerciseCategory);
      setSelectedType(generatedType!);
    }

    setCommonFields((previousFields) => ({
      ...previousFields,
      instructions: draft.exercise?.instructions || previousFields.instructions,
      additionalInfo: draft.exercise?.additionalInfo || previousFields.additionalInfo,
    }));

    if (draft.exercise.configPatch && typeof draft.exercise.configPatch === 'object') {
      setExerciseConfig((previousConfig: any) => ({
        ...(isChangingType ? {} : previousConfig),
        ...draft.exercise?.configPatch,
      }));
      setConfigRenderVersion((previousVersion) => previousVersion + 1);
    }
  };

  const handleSaveExercise = async () => {
    console.log('handleSaveExercise called!');
    if (!parsedLessonId || !selectedType || !exerciseConfig || Object.keys(exerciseConfig).length === 0) return;

    console.log('commonFields before save:', commonFields);

    const { materialId, ...configForSave } = exerciseConfig;

    const exerciseData: ExerciseUpsertPayload = {
      type: selectedType,
      config: JSON.stringify(configForSave), // Convert config to string
      instructions: commonFields.instructions,
      additionalInfo: commonFields.additionalInfo,
      order: commonFields.order,
      maxScore: commonFields.maxScore,
      requiresManualChecking: commonFields.requiresManualChecking,
      allowReset: commonFields.allowReset,
      showCorrectAnswers: commonFields.showCorrectAnswers,
      materialId,
      showMaterialToStudent: configForSave.showMaterialToStudent,
    };

    console.log('exerciseData before sending to backend:', exerciseData);

    try {
      await createExercise(parsedLessonId, exerciseData);
      navigate(`/course/${courseId}/lesson/${lessonId}`); // Navigate back to the lesson page
    } catch (error) {
      console.error('Failed to save exercise:', error);
    }
  };

  const renderExerciseConfig = () => {
    switch (selectedType) {
      case 'math_arit_2operand_1phase':
        return <MathArit2Operand1PhaseConfig onConfigChange={handleConfigChange} initialConfig={exerciseConfig} />;
      case 'math_arit_rounding_precision':
        return <MathAritRoundingConfig onConfigChange={handleConfigChange} initialConfig={exerciseConfig} />;
      case 'math_arit_2operand_2phase':
        return <MathArit2Operand1PhaseConfig onConfigChange={handleConfigChange} initialConfig={exerciseConfig} />;
      case 'fill_in_the_blanks':
        return <FillInBlanksConfig onConfigChange={handleConfigChange} initialConfig={exerciseConfig} />;
      case 'pairing':
        return <PairingConfig onConfigChange={handleConfigChange} initialConfig={exerciseConfig} />;
      case 'material_based_ai_assisted':
        if (!parsedLessonId) {
          return <Typography variant="body1">Oppitunnin tunniste puuttuu</Typography>;
        }
        return <MaterialBasedConfig onConfigChange={handleConfigChange} initialConfig={exerciseConfig} lessonId={parsedLessonId} />;
      case 'material_based_multichoice':
        if (!parsedLessonId) {
          return <Typography variant="body1">Oppitunnin tunniste puuttuu</Typography>;
        }
        return <MaterialBasedMultichoiceConfig onConfigChange={handleConfigChange} initialConfig={exerciseConfig} lessonId={parsedLessonId} />;
      case 'reading_comprehension_multichoice':
        if (!parsedLessonId) {
          return <Typography variant="body1">Oppitunnin tunniste puuttuu</Typography>;
        }
        return <MaterialBasedMultichoiceConfig
          onConfigChange={handleConfigChange}
          initialConfig={exerciseConfig}
          lessonId={parsedLessonId}
          exerciseType="reading_comprehension_multichoice"
          generationProfile="reading_comprehension_fi"
        />;
      case 'true_false':
        if (!parsedLessonId) {
          return <Typography variant="body1">Oppitunnin tunniste puuttuu</Typography>;
        }
        return <MaterialBasedMultichoiceConfig
          onConfigChange={handleConfigChange}
          initialConfig={exerciseConfig}
          lessonId={parsedLessonId}
          exerciseType="true_false"
          generationProfile="true_false"
          fixedOptionCount={2}
        />;
      case 'sort_to_boxes':
        return <SortToBoxesConfig onConfigChange={handleConfigChange} initialConfig={exerciseConfig} />;
      case 'ordering':
        return <OrderingConfig onConfigChange={handleConfigChange} initialConfig={exerciseConfig} />;
      default:
        return <Typography variant="body1">Valitse harjoituksen tyyppi</Typography>;
    }
  };

  return (
    <Container maxWidth={false} disableGutters>
      {/* Breadcrumb Navigation */}
      {courseId && <Breadcrumb courseId={courseId} lessonId={lessonId} additionalCrumb="Uusi harjoitus *" />}
      <Box
        mt={3}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 3,
          flexDirection: { xs: 'column', xl: 'row' },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          <Typography variant="h4" gutterBottom>
            Luo uusi harjoitus
          </Typography>

          <Box mb={4}>
            <ExerciseCategorySelection
              categories={exerciseCategories}
              onSelectCategory={handleSelectCategory}
            />
          </Box>

          {selectedCategory && (
            <Box mb={4}>
              <ExerciseTypeSelection
                exerciseTypes={exerciseTypesData.filter(
                  (type) => type.exerciseCategory === selectedCategory
                )}
                onSelectType={handleSelectType}
              />
            </Box>
          )}

          {selectedType && (
            <Box mb={4}>
              <ExerciseCommonFields
                instructions={commonFields.instructions}
                additionalInfo={commonFields.additionalInfo}
                order={commonFields.order}
                maxScore={commonFields.maxScore}
                requiresManualChecking={commonFields.requiresManualChecking}
                allowReset={commonFields.allowReset}
                showCorrectAnswers={commonFields.showCorrectAnswers}
                onChange={handleCommonFieldChange}
              />
            </Box>
          )}

          {selectedType && parsedLessonId && !usesBuiltInMaterialSelection && (
            <Box mb={4}>
              <ExerciseMaterialSelector
                lessonId={parsedLessonId}
                materialId={exerciseConfig.materialId}
                showMaterialToStudent={exerciseConfig.showMaterialToStudent}
                onChange={handleMaterialChange}
              />
            </Box>
          )}

          {selectedType && (
            <Box mb={4} key={`exercise-config-${selectedType}-${configRenderVersion}`}>
              {renderExerciseConfig()}
            </Box>
          )}

          {selectedType && (
            <Box mb={4}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  console.log('Save button clicked!');
                  handleSaveExercise();
                }}
              >
                Tallenna harjoitus
              </Button>
            </Box>
          )}
        </Box>

        <AuthoringAssistantPanel
          courseId={parsedCourseId}
          lessonId={parsedLessonId || undefined}
          draftExercise={{
            type: selectedType,
            instructions: commonFields.instructions,
            additionalInfo: commonFields.additionalInfo,
            config: exerciseConfig,
          }}
          draftTarget="exercise"
          onApplyDraft={handleApplyDraft}
          initialPrompt={assistantCreateState.prompt}
          autoGenerateInitialDraft={assistantCreateState.autoGenerate}
        />
      </Box>
    </Container>
  );
};

export default ExerciseCreatePage;
